import React from 'react';
import { Link } from 'react-router-dom';

interface ModuleCardProps {
  title: string;
  description: string;
  icon: string;
  path: string;
  features: string[];
}

const ModuleCard: React.FC<ModuleCardProps> = ({ title, description, icon, path, features }) => (
  <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
    <div className="p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-sm text-gray-600 mb-4">{description}</p>
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center text-sm text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {feature}
              </li>
            ))}
          </ul>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
        </svg>
      </div>
      <div className="mt-6">
        <Link
          to={path}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full"
        >
          Configurar
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  </div>
);

export const DocumentsConfigPage: React.FC = () => {
  const modules: ModuleCardProps[] = [
    {
      title: "Extensões e Limites",
      description: "Gerencie as extensões permitidas e seus limites de upload",
      icon: "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4",
      path: "/admin/documents/extensions",
      features: [
        "Cadastro dinâmico de extensões",
        "Limites personalizados por tipo",
        "Bloqueio de extensões perigosas",
        "Configurações de verificação"
      ]
    },
    {
      title: "Tipos de Documento",
      description: "Configure os diferentes tipos de documentos e seus layouts",
      icon: "M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z",
      path: "/admin/documents/types",
      features: [
        "Editor WYSIWYG para layouts",
        "Metadados customizáveis",
        "Campos dinâmicos",
        "Modelos de documento"
      ]
    },
    {
      title: "Tipos de Relacionamento",
      description: "Cadastre os tipos de relacionamento entre documentos",
      icon: "M8 10h.01M12 10h.01M16 10h.01M3 5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-7l-4 4v-4H5a2 2 0 01-2-2V5z",
      path: "/admin/documents/relationship-types",
      features: [
        "Tipos sistêmicos protegidos",
        "Tipos customizáveis",
        "Ativar e desativar tipos",
        "Bloqueio de exclusão em uso"
      ]
    },
    {
      title: "Retenção e Arquivamento",
      description: "Defina políticas de retenção e arquivamento de documentos",
      icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
      path: "/admin/documents/retention",
      features: [
        "Períodos de retenção",
        "Políticas de arquivamento",
        "Notificações de expiração",
        "Regras de exclusão"
      ]
    },
    {
      title: "Assinatura Digital",
      description: "Configure as políticas de assinatura digital de documentos",
      icon: "M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z",
      path: "/admin/documents/signature",
      features: [
        "Assinatura simples e avançada",
        "Certificados digitais",
        "Validação e verificação",
        "Fluxo de assinaturas"
      ]
    },
    {
      title: "Processamento Inteligente",
      description: "Configure as funcionalidades de IA para documentos",
      icon: "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
      path: "/admin/documents/processing",
      features: [
        "Extração automática de metadados",
        "Classificação por IA",
        "Análise semântica",
        "Resumo automático"
      ]
    }
  ];

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Configuração de Documentos</h1>
        <p className="mt-1 text-sm text-gray-600">
          Configure extensões, tipos, relacionamentos, retenção e demais opções do módulo de documentos. Fluxos de aprovação e calendário corporativo ficam no menu Workflow.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {modules.map((module, index) => (
          <ModuleCard key={index} {...module} />
        ))}
      </div>
    </div>
  );
}; 